import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

const TOUR_STEPS = [
  {
    target: '[data-testid="business-selector"]',
    title: "Business Selector",
    content: "Select your business here. All data is organized per business.",
    placement: "right",
  },
  {
    target: '[data-testid="nav-section-bookkeeping"]',
    title: "Bookkeeping",
    content: "Manage transactions, import from QuickBooks, and auto-categorize expenses.",
    placement: "right",
  },
  {
    target: '[data-testid="nav-section-financial"]',
    title: "Financial Health",
    content: "Track allocations, reserves, cash flow, and profit & loss.",
    placement: "right",
  },
  {
    target: '[data-testid="nav-section-tax"]',
    title: "Tax Planning",
    content: "Calculate estimates, track mileage, and manage your home office deduction.",
    placement: "right",
  },
  {
    target: '[data-testid="nav-section-deductions"]',
    title: "Deductions",
    content: "Discover deductions you might be missing from our library of 475+ business deductions.",
    placement: "right",
  },
  {
    target: '[data-testid="toggle-sidebar"]',
    title: "Collapse Sidebar",
    content: "Collapse the sidebar anytime to get more screen space.",
    placement: "bottom",
  },
];

const OnboardingTour = ({ run, onComplete }) => {
  const [step, setStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [spotlightRect, setSpotlightRect] = useState(null);

  const positionTooltip = useCallback(() => {
    if (!run) return;
    const current = TOUR_STEPS[step];
    const el = document.querySelector(current.target);
    if (!el) return;

    // Scroll the target into view if it's outside the viewport
    const elRect = el.getBoundingClientRect();
    if (elRect.top < 0 || elRect.bottom > window.innerHeight) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Re-measure after scroll
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        updatePositions(rect, current.placement);
      }, 350);
      return;
    }

    updatePositions(elRect, current.placement);
  }, [run, step]);

  const updatePositions = (rect, placement) => {
    setSpotlightRect({
      top: rect.top - 4,
      left: rect.left - 4,
      width: rect.width + 8,
      height: rect.height + 8,
    });

    const gap = 12;
    const tooltipWidth = 300;
    let top, left;

    if (placement === "right") {
      top = rect.top;
      left = rect.right + gap;
    } else if (placement === "bottom") {
      top = rect.bottom + gap;
      left = rect.left;
    } else {
      top = rect.bottom + gap;
      left = rect.left;
    }

    // Keep tooltip within viewport
    if (top + 200 > window.innerHeight) {
      top = Math.max(16, window.innerHeight - 220);
    }
    if (left + tooltipWidth > window.innerWidth) {
      left = Math.max(16, rect.left - tooltipWidth - gap);
    }
    if (top < 16) top = 16;

    setTooltipPos({ top, left });
  };

  useEffect(() => {
    if (!run) return;
    positionTooltip();
    window.addEventListener("resize", positionTooltip);
    return () => window.removeEventListener("resize", positionTooltip);
  }, [run, step, positionTooltip]);

  const finish = useCallback(() => {
    localStorage.setItem("opturna_tour_complete", "true");
    if (onComplete) onComplete();
  }, [onComplete]);

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else finish();
  };
  const handleBack = () => { if (step > 0) setStep(step - 1); };
  const handleSkip = () => finish();

  if (!run) return null;

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  return createPortal(
    <div data-testid="onboarding-tour-overlay" style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
      {/* Dark overlay with spotlight cutout — pointer-events none so underlying UI is clickable */}
      {spotlightRect && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
            clipPath: `polygon(
              0% 0%, 0% 100%, ${spotlightRect.left}px 100%,
              ${spotlightRect.left}px ${spotlightRect.top}px,
              ${spotlightRect.left + spotlightRect.width}px ${spotlightRect.top}px,
              ${spotlightRect.left + spotlightRect.width}px ${spotlightRect.top + spotlightRect.height}px,
              ${spotlightRect.left}px ${spotlightRect.top + spotlightRect.height}px,
              ${spotlightRect.left}px 100%, 100% 100%, 100% 0%
            )`,
            pointerEvents: "none",
          }}
        />
      )}
      {/* Spotlight border */}
      {spotlightRect && (
        <div style={{
          position: "fixed",
          top: spotlightRect.top,
          left: spotlightRect.left,
          width: spotlightRect.width,
          height: spotlightRect.height,
          border: "2px solid #1e3a5f",
          borderRadius: 8,
          pointerEvents: "none",
          zIndex: 10001,
        }} />
      )}
      {/* Tooltip — this needs pointer-events so buttons work */}
      <div
        data-testid="onboarding-tooltip"
        style={{
          position: "fixed",
          top: tooltipPos.top,
          left: tooltipPos.left,
          maxWidth: 300,
          background: "#fff",
          borderRadius: 10,
          padding: "16px 20px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          zIndex: 10002,
          fontSize: 13,
          color: "#1e3a5f",
          pointerEvents: "auto",
        }}
      >
        <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{current.title}</p>
        <p style={{ color: "#5a6577", lineHeight: 1.5, marginBottom: 14 }}>{current.content}</p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            onClick={handleSkip}
            data-testid="tour-skip-btn"
            style={{ background: "none", border: "none", color: "#8b95a5", fontSize: 12, cursor: "pointer" }}
          >
            Skip tour
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#8b95a5" }}>{step + 1}/{TOUR_STEPS.length}</span>
            {step > 0 && (
              <button
                onClick={handleBack}
                data-testid="tour-back-btn"
                style={{ background: "none", border: "none", color: "#5a6577", fontSize: 13, cursor: "pointer", padding: "4px 8px" }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              data-testid="tour-next-btn"
              style={{
                background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 6,
                fontSize: 13, padding: "8px 16px", cursor: "pointer",
              }}
            >
              {isLast ? "Got it!" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingTour;
